import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { ConversionPageLink } from '@/components/Tracking'
import { fetchInstitutionDonorOrderNumbers } from '@/lib/institution-donors'
import type {
  InstitutionDonorOrder,
  InstitutionDonorOrderNumbersResponse,
} from '@/types/institution-donor'
import styles from './DonorLuckyNumbersSection.module.css'

const orderDate = (date: string | null) =>
  date
    ? new Date(date).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' })
    : 'Data não informada'
const orderMoney = (amount: number) =>
  amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

type Props = {
  order: InstitutionDonorOrder
  donorId: number
  token?: string | null
  canCancel: boolean
  onCancel: () => void
}

export function DonorOrderCard({ order, donorId, token, canCancel, onCancel }: Props) {
  const [expanded, setExpanded] = useState(false)
  const [data, setData] = useState<InstitutionDonorOrderNumbersResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const active = useRef(true)
  const pending = useRef(false)
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])
  const canceled = order.status === 'canceled'

  const load = async () => {
    if (pending.current) return
    pending.current = true
    setLoading(true)
    setError(false)
    try {
      const result = await fetchInstitutionDonorOrderNumbers(
        donorId,
        order.id,
        (data?.pagination.current_page ?? 0) + 1,
        token
      )
      if (active.current)
        setData((previous) => ({
          ...result,
          numbers: [...(previous?.numbers ?? []), ...result.numbers],
        }))
    } catch {
      if (active.current) setError(true)
    } finally {
      pending.current = false
      if (active.current) setLoading(false)
    }
  }

  return (
    <article className={`${styles.order} ${canceled ? styles.canceled : ''}`}>
      <button
        type="button"
        className={styles.orderHeading}
        aria-expanded={expanded}
        aria-controls={`order-${order.id}-numbers`}
        onClick={() => {
          setExpanded(!expanded)
          if (!expanded && !data && !loading) void load()
        }}
      >
        <Icon icon={expanded ? 'mdi:chevron-down' : 'mdi:chevron-right'} aria-hidden />
        <span className={styles.orderIdentity}>
          <strong>Pedido #{order.id}</strong>
          <span>{orderDate(order.created_at)}</span>
        </span>
        <span className={styles.orderAmount}>
          {orderMoney(order.amount)}
          <small>
            {order.numbers_count} {canceled ? 'números devolvidos' : 'números'}
          </small>
        </span>
        <span className={canceled ? styles.canceledBadge : styles.paidBadge}>
          {canceled ? 'Cancelado' : 'Pago'}
        </span>
      </button>
      <div
        id={`order-${order.id}-numbers`}
        hidden={!expanded}
        className={styles.orderBody}
      >
        {canceled && (
          <p className={styles.historyNote}>
            Cancelado{order.canceled_at ? ` em ${orderDate(order.canceled_at)}` : ''}. Os
            números abaixo são históricos e não pertencem mais a este pedido. Este status
            não confirma estorno financeiro.
          </p>
        )}
        {data && (
          <div className={styles.numberGrid}>
            {data.numbers.map((number) => (
              <span key={number} className={styles.number}>
                {number}
              </span>
            ))}
          </div>
        )}
        {data && !data.numbers.length && (
          <p className={styles.status}>
            {canceled
              ? 'Nenhum número registrado no histórico deste cancelamento.'
              : 'Nenhum número atribuído a este pedido até o momento.'}
          </p>
        )}
        {loading && (
          <p role="status" className={styles.status}>
            Carregando números…
          </p>
        )}
        {error && (
          <p role="alert" className={styles.error}>
            Não foi possível carregar os números.
          </p>
        )}
        {(error ||
          (data && data.pagination.current_page < data.pagination.last_page)) && (
          <button
            type="button"
            className={styles.moreButton}
            disabled={loading}
            onClick={() => void load()}
          >
            {error
              ? 'Tentar novamente'
              : `Ver mais números (${data?.numbers.length} de ${data?.pagination.total})`}
          </button>
        )}
        <div className={styles.orderOrigin}>
          <span className={styles.orderOriginLabel}>URL de origem do pedido</span>
          <ConversionPageLink
            display={order.conversion_page_url_display ?? order.conversion_page_url}
            rawUrl={order.conversion_page_url}
          />
        </div>
        {canCancel && !canceled && (
          <div className={styles.orderActions}>
            <button type="button" className={styles.cancelButton} onClick={onCancel}>
              <Icon icon="mdi:shield-lock-outline" aria-hidden />
              Cancelar pedido
            </button>
          </div>
        )}
      </div>
    </article>
  )
}
